import React, { useState, useEffect, useGlobal } from "reactn";
import PropTypes from "prop-types";

import Loader from "shared-anpost-components/components/loader/Loader";
import {Tablist, TablistHeader, TablistContent, TabButton} from "../../_common/tablist/Tablist.styled";
import DeliveryItemsGrid from "./grid/DeliveryItemsGrid";
import NoItemsCTA from "./NoItemsMessage/NoItemsCTA";
import BottomLink from "./BottomLink/BottomLink";
import MyDeliveriesHooks from "../../../hooks/myDeliveriesHooks";
import { getJWTInfo, getTrackingItems } from "../../../services/Api";

import { receivingItemsTestData } from "../../../config/TrackingItems.config";

const tabsArr = [
    { key: "receiving", label: "Receiving" },
    { key: "sending", label: "Sending" }
];

const MyDeliveriesSummary = ({ userProfile }) => {
    const [activeTab, setActiveTab] = useState("receiving");
    const [isBusiness, setIsBusiness] = useGlobal("isBusiness");
    const [isCustomerRegistered] = useGlobal("mydelsRegistered");
    const [allItemsEmpty, setAllItemsEmpty] = useState(false);
    const [trackingListLoading, setTrackingListLoading] = useState(true);
    const [receivingItemsData, setReceivingItemsData] = useState([]);
    const [sendingItemsData, setSendingItemsData] = useState([]);

	const {
        filterReceivingItems,
        filterSendingItems
	} = MyDeliveriesHooks({ userProfile });

    const checkForEmptyItemsState = () => {
		
        if((receivingItemsData.length !== 0 && sendingItemsData.length !== 0)
		 || receivingItemsData.length !== 0 && sendingItemsData.length === 0) {
			// if both tabs are not empty or only sending is empty 
            setAllItemsEmpty(false);
            setActiveTab("receiving");
        } else if (receivingItemsData.length === 0 && sendingItemsData.length !== 0) {
			// if only receiving is empty
			setAllItemsEmpty(false);
			setActiveTab("sending");
		} else {
			// if both tabs are empty
			setAllItemsEmpty(true);
			setActiveTab("");
		}
    }

    const renderTabs = tabs => {
        return tabs.map(tab => {
            return (
                <TabButton
                    key={tab.key}
                    active={activeTab === tab.key}
                    aria-selected={activeTab === tab.key}
                    aria-controls={`${tab.key}Tab`}
                    onClick={e => {
                        e.preventDefault();
                        setActiveTab(tab.key);
                    }}
                    role="tab"
                    disabled={allItemsEmpty}
                    className="gtm-tab"
                    isBusiness={isBusiness}
                >
                    {tab.label}
					{tab.key === "receiving" && receivingItemsData.length > 0 && 
						<span>{receivingItemsData.length}</span>
					}
					{tab.key === "sending" && sendingItemsData.length > 0 && 
						<span>{sendingItemsData.length}</span>
					}
                </TabButton>
            );
        });
    };

    const renderReceivingTab = () => { 
        return (
            <DeliveryItemsGrid
                data={receivingItemsData}
                noItemsMsg={allItemsEmpty ? 
                "There's been nothing sent recently via An Post." : 
                `An Post has no post for you at the moment. Please check back later.`}
                role="tabpanel"
                id="receivingTab"
                loading={trackingListLoading}
                ariaLabelledby="receivingTab"
                activeTab={activeTab}
            />
        );
    };

    const renderSendingTab = () => {
        return (
            <DeliveryItemsGrid 
				data={sendingItemsData}
				noItemsMsg={allItemsEmpty ? 
                "There's been nothing sent recently via An Post." : 
                "You haven't sent anything or returned shopping recently."} 
				role="tabpanel" 
				id="sendingTab" 
				loading={trackingListLoading} 
				ariaLabelledby="sendingTab"
                activeTab={activeTab}
			/>
        );
    };

    useEffect(() => {
        checkForEmptyItemsState();

		// if business and both tabs are populated, sending tab should be open by default
		if (isBusiness && receivingItemsData.length > 0 && sendingItemsData.length > 0) {
			setActiveTab("sending");
		}
    }, [receivingItemsData, sendingItemsData, isBusiness]);

    useEffect(() => {
        if (isCustomerRegistered) {
            const { uid } = userProfile;

            getJWTInfo().then(() => {
                getTrackingItems(uid)
                    .then(response => {
                        if (response.status === 200) {
                            setTrackingListLoading(false);
                            if (response && response.data && response.data.trackingItems && !receivingItemsData.length) {
                                const { trackingItems } = !window.apDevMode ? response.data : receivingItemsTestData[0];
                                setReceivingItemsData(filterReceivingItems(trackingItems));
                                setSendingItemsData(filterSendingItems(trackingItems));
                            }                
                        }
                    })
                    .catch(() => {
                        setTrackingListLoading(false);
                    })
            });    
        }
    }, [isCustomerRegistered]);

    return (
		<>
            <h5>My deliveries</h5>
			<Tablist>
				<TablistHeader emptyItems={allItemsEmpty} isBusiness={isBusiness} role="tablist" aria-label="Deliveries">
					{renderTabs(tabsArr)}
				</TablistHeader>
				{trackingListLoading ? 
					<Loader local /> :
					<TablistContent role="tabpanel">
						{activeTab === "receiving" && renderReceivingTab()}
						{activeTab === "sending" && renderSendingTab()}
						{activeTab === "" && <NoItemsCTA activeTab={activeTab} noItemsMsg="There's been nothing sent recently via An Post."/>}
						<BottomLink activeTab={activeTab} className="cta-link gtm-linkclick"/>
					</TablistContent>
				}
			</Tablist>
		</>
    );
};

export default MyDeliveriesSummary;

MyDeliveriesSummary.propTypes = {
    userProfile: PropTypes.object
};
